/**
 * WHAT THE BOOK SAYS ABOUT ITSELF, AND WHAT THE PLAYER IS AFTER.
 *
 * Words only. Kept apart from any canvas for the reason `src/render/chart-copy.ts`
 * is kept apart from its painter: the wording is the part that can be read, argued
 * with and tested, and it keeps that file's three rules rather than starting a
 * second voice on the same sheet.
 *
 *   IT IS A PHRASE, NOT A FIELD. "14 species landed, 71.7 tonnes in all" and never
 *   "SPECIES: 14  WEIGHT: 71.7t". A label-and-value pair is an instrument panel.
 *   IT SPEAKS IN THE INTERFACE'S VOICE. Sentence case, one sentence, no apology
 *   and no exclamation.
 *   IT NAMES NOTHING THE PLAYER HAS NOT FOUND - and here that is not about places.
 *   It is about a record that may not have been set. See `personalBest`.
 */

import { SPECIES, bestOverall, speciesById, type SimState } from '../sim/index';

/**
 * Grams as a person would say them, in the units the reader can hold.
 *
 * Three registers, and the break points are what the engine actually produces
 * rather than round numbers picked for tidiness: a single fish is grams to
 * kilograms (the species table's bands run from a minnow's tens of grams to a
 * bluefin's 442 kg), and a save's lifetime haul crosses a tonne within the first
 * few hours of idle fishing and never comes back.
 *
 * ONE DECIMAL BELOW A HUNDRED AND NONE ABOVE IT, in every register. The decimal is
 * there to separate 4.2 kg from 4.9 kg, which is a difference a player cares about;
 * at 340 kg the tenth is noise on a figure nobody reads that closely, and carrying
 * it makes the line longer for nothing.
 */
export function weightWords(grams: number): string {
  if (!Number.isFinite(grams) || grams <= 0) return '0 g';
  if (grams < 1000) return `${Math.round(grams)} g`;
  if (grams < 1_000_000) return `${figure(grams / 1000)} kg`;
  return `${figure(grams / 1_000_000)} tonnes`;
}

/** One decimal below a hundred, whole above it, with thousands grouped. */
function figure(v: number): string {
  if (v < 100) {
    const tenths = Math.round(v * 10) / 10;
    return tenths.toFixed(1);
  }
  return group(Math.round(v));
}

/** 71745 -> "71,745". The hand carries a comma; the tally face does not, and this
 *  line is written in the hand, so the separator is legal here and nowhere else. */
function group(n: number): string {
  const s = String(n);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return out;
}

/**
 * A COUNT, SAID THE WAY A LEDGER WRITES ONE, and rounded for a reason that is not taste.
 *
 * The foot of the sheet stands there for the whole session, and the host repaints the paper
 * whenever a pixel of it would differ. An exact coin figure changes on every catch, so an
 * exact foot is a sheet redrawn several times a second for the life of the save. Above ten
 * thousand this moves once per hundred, above a million once per hundred thousand, which is
 * the difference between a standing account that costs nothing and one that costs the idle
 * bar this project is measured against.
 *
 * It is also simply what somebody writing in a log does: nobody records 4,582,651 by hand.
 * Under ten thousand it IS exact, because the first coins of a save are counted one by one
 * and rounding them away would be rounding away the only progress there is.
 */
export function countWords(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  const v = Math.trunc(n);
  if (v < 10_000) return group(v);
  if (v < 1_000_000) return group(Math.round(v / 100) * 100);
  return `${figure(v / 1_000_000)} million`;
}

/** How many of the sea's species this save has landed at least one of. */
export function speciesLanded(state: SimState): number {
  let n = 0;
  for (const s of SPECIES) if ((state.catches[s.id] ?? 0) > 0) n++;
  return n;
}

/**
 * THE PERSONAL BEST, AND THE DOORBELL THAT DID NOT RING.
 *
 * CONTRACT 1.5l(a) rules that mastery deepens the species it is spent on and that "the ledger
 * records a personal best". When this file was first written no such field existed, so it
 * carried a PROBE instead of a read: an untyped look for a scalar named `recordG`, with a
 * test beside it asserting the field was absent, so that "the day the field lands this test
 * fails and the clause is switched on deliberately rather than by accident".
 *
 * The field landed. The doorbell did not ring, and the reason is written into the probe's own
 * last paragraph: it was bound to a NAME and a SHAPE. `SimState.records` arrived as a
 * per-species map, `'recordG' in state` stayed false, the absence test stayed green, and a
 * quantity the engine had begun to earn and persist went unrendered with the whole suite
 * passing. A fence keyed to the name of a thing that does not exist yet is a fence keyed to a
 * guess about how it will arrive.
 *
 * So this is a plain read now, and the fence that replaces the absence test is bound to the
 * CONCEPT: earned-is-rendered.test.ts derives which of `FIELD_CLASS.earned` are per-species
 * maps from a real state, exactly as never-nerf.test.ts does, and requires each one to move a
 * pixel of the interface. A fourth earned map will fail it on the day it is added, whatever
 * it is called and whatever shape it comes in.
 */
export function personalBest(state: SimState): { readonly name: string; readonly weightG: number } | null {
  const best = bestOverall(state.records);
  // `bestOverall` compares with `>`, which an infinity wins, and `openSave` mints earned
  // numbers out of `unknown`. A surface must not print a weight that is not a weight, so the
  // finiteness check lives at the point of writing rather than being assumed upstream.
  if (best === null || !Number.isFinite(best.weightG) || best.weightG <= 0) return null;
  const species = speciesById(best.id);
  // A record under a key this build does not ship is kept by the loader on purpose - a save
  // from a later version must not be quietly trimmed - but it cannot be NAMED here, so the
  // book falls back to the weight alone rather than inventing a fish.
  return { name: species === undefined ? '' : species.name.toLowerCase(), weightG: best.weightG };
}

/**
 * THE LINE THE OPEN BOOK WRITES ABOUT ITSELF.
 *
 * It appears on the sheet's second line and ONLY while the ledger is lifted, and it
 * stays that way for a reason that survived the round that added the standing foot:
 * the foot answers "how am I doing" at a glance, and this answers "what is in the
 * book" at length. A save's whole history does not belong on a line somebody reads
 * out of the corner of their eye while watching the sea.
 *
 * The empty save gets a sentence too, and it is an invitation rather than a zero.
 */
export function bookLine(state: SimState): string {
  const n = speciesLanded(state);
  if (n === 0) return 'Nothing in the book yet.';
  const best = personalBest(state);
  const head = `${n} species landed, ${weightWords(state.weightG)} in all`;
  if (best === null) return `${head}.`;
  // The record NAMES ITS FISH here and carries only its weight on the strip's pole, because
  // the pole already draws the animal and the book does not.
  const which = best.name === '' ? weightWords(best.weightG) : `${weightWords(best.weightG)} of ${best.name}`;
  return `${head}, the best of them ${which}.`;
}

/**
 * WHAT THE BOAT IS AFTER, WHEN THAT WAS A DECISION.
 *
 * `quarryOf` in chart-copy draws the target if one is set and otherwise the species
 * the save has landed most of, and the strip has no way of saying which of those
 * two it is showing. So a player who has never touched the control sees a fish on
 * the sheet and cannot tell it apart from a fish they chose - which empties the one
 * verb the game has of the only feedback that makes it feel like a verb.
 *
 * The answer is a clause and not a mark. The sheet's whole vocabulary is somebody's
 * handwriting, "after cod" is what a person actually says about the thing they are
 * fishing for, and a clause costs no pixel of geometry that could then drift from
 * the painter's.
 *
 * IT IS NOW UNCONDITIONAL, and that is a correction. The first build appended the
 * clause only to a phrase that had not ended, on the argument that "Making
 * north-west, 2 hours off, after cod" is three clauses to say two facts. True about
 * the comma, wrong about the fact: a boat under orders is under orders for hours,
 * and for all of them the one thing the player chose was invisible. The clause
 * becomes its own short sentence there instead, which costs a full stop and keeps
 * the only evidence the game gives that `setTarget` did anything.
 */
export function afterClause(phrase: string, state: SimState): string {
  const id = state.targetSpecies;
  if (id === '') return phrase;
  const species = SPECIES.find((s) => s.id === id);
  if (species === undefined) return phrase;
  const after = `after ${species.name.toLowerCase()}`;
  return phrase.endsWith('.') ? `${phrase} ${after.charAt(0).toUpperCase()}${after.slice(1)}.` : `${phrase}, ${after}`;
}
